import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Truck, Package, Clock, CheckCircle, Loader2, ArrowUpRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { getCurrentTransport, getTransportStats, getMyShipments } from "@/Calls/callTransportAuth";

interface Stats { pending: number; accepted: number; in_transit: number; completed: number; total: number; }
interface Company { company_name: string; email: string; mc_number?: string; dot_number?: string; }
interface Shipment {
    id: number; vehicle_vin: string | null; pickup_location: string | null;
    dropoff_location: string | null; status: string; scheduled_date: string | null;
    created_at: string;
}

const STATUS_COLORS: Record<string, string> = {
    pending:    "bg-yellow-100 text-yellow-700",
    accepted:   "bg-blue-100 text-blue-700",
    in_transit: "bg-indigo-100 text-indigo-700",
    completed:  "bg-green-100 text-green-700",
    cancelled:  "bg-gray-100 text-gray-500",
};

export default function TransportDashboardPage() {
    const [company,   setCompany]   = useState<Company | null>(null);
    const [stats,     setStats]     = useState<Stats | null>(null);
    const [shipments, setShipments] = useState<Shipment[]>([]);
    const [loading,   setLoading]   = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentTransport().then((r) => setCompany(r.data.company)),
            getTransportStats().then((d)    => setStats(d.stats)),
            getMyShipments().then((d)       => setShipments((d.shipments ?? []).slice(0, 8))),
        ])
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Transport Dashboard</h1>
                <p className="text-muted-foreground text-sm mt-0.5">{company?.company_name}</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: "Total Shipments",  value: stats?.total ?? 0,     icon: Truck,       color: "text-blue-600",   bg: "bg-blue-50"   },
                    { label: "Pending",           value: stats?.pending ?? 0,   icon: Clock,       color: "text-yellow-600", bg: "bg-yellow-50" },
                    { label: "In Transit",        value: stats?.in_transit ?? 0,icon: Package,     color: "text-indigo-600", bg: "bg-indigo-50" },
                    { label: "Completed",         value: stats?.completed ?? 0, icon: CheckCircle, color: "text-green-600",  bg: "bg-green-50"  },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                    <Card key={label} className="p-4">
                        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                            <Icon className={`h-5 w-5 ${color}`} />
                        </div>
                        <p className="text-2xl font-bold">{value}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                    </Card>
                ))}
            </div>

            {/* Recent Shipments */}
            <Card className="overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b">
                    <h2 className="font-semibold">Recent Shipments</h2>
                    <Link to="/transport/shipments" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                        View all <ArrowUpRight className="h-3 w-3" />
                    </Link>
                </div>
                <div className="divide-y">
                    {shipments.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-8">No shipments yet</p>
                    ) : shipments.map((s) => (
                        <div key={s.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/40">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                                    <Truck className="h-4 w-4 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium">
                                        {s.pickup_location ?? "—"} → {s.dropoff_location ?? "—"}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                        {s.vehicle_vin ? `VIN: ${s.vehicle_vin}` : "No VIN"} · #{s.id}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {s.scheduled_date && (
                                    <span className="text-xs text-muted-foreground">
                                        {new Date(s.scheduled_date).toLocaleDateString()}
                                    </span>
                                )}
                                <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${STATUS_COLORS[s.status] ?? "bg-gray-100 text-gray-600"}`}>
                                    {s.status.replace("_", " ").toUpperCase()}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            {/* Company Info */}
            {company && (
                <Card className="p-5">
                    <h2 className="font-semibold mb-3">Company Info</h2>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="text-muted-foreground">Email:</span> <span className="ml-2">{company.email}</span></div>
                        {company.mc_number  && <div><span className="text-muted-foreground">MC#:</span>  <span className="ml-2">{company.mc_number}</span></div>}
                        {company.dot_number && <div><span className="text-muted-foreground">DOT#:</span> <span className="ml-2">{company.dot_number}</span></div>}
                    </div>
                </Card>
            )}
        </div>
    );
}
